// SpotHeroReportTable.js
import React, { useState } from "react";
import { Table, Collapse, Button } from "reactstrap";
import PropTypes from "prop-types";
import formatCurrency from "constants/formatCurrency";
import formatDate from "constants/formatDate";

const SpotHeroReportTable = ({ locations }) => {
    const [expanded, setExpanded] = useState({});
    const [page, setPage] = useState({});
    const itemsPerPage = 5;

    const toggleExpand = (id) => {
        setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const paginate = (arr, locId) => {
        const currentPage = page[locId] || 1;
        const start = (currentPage - 1) * itemsPerPage;
        return arr.slice(start, start + itemsPerPage);
    };

    const totalPages = (arr) => Math.max(1, Math.ceil(arr.length / itemsPerPage));

    return (
        <div className="overflow-x-auto">
            <Table bordered responsive className="align-middle table-nowrap">
                <thead className="table-light">
                    <tr>
                        <th>Location</th>
                        <th>Transactions</th>
                        <th>Total Remit</th>
                        <th>Total Sale Price</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {locations
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .filter(item => item.transactions.length > 0)
                        .map((loc) => {
                            const totals = loc.transactions.reduce(
                                (acc, t) => {
                                    acc.gross_remit += t.gross_remit || 0;
                                    acc.reservation_remit += t.reservation_remit || 0;
                                    acc.oversize_fee_remit += t.oversize_fee_remit || 0;
                                    acc.total_remit += t.total_remit || 0;
                                    acc.total_sale_price += t.total_sale_price || 0;
                                    return acc;
                                },
                                {
                                    gross_remit: 0,
                                    reservation_remit: 0,
                                    oversize_fee_remit: 0,
                                    total_remit: 0,
                                    total_sale_price: 0,
                                }
                            );

                            const currentPage = page[loc.id] || 1;
                            const paginated = paginate(loc.transactions, loc.id);

                            return (
                                <React.Fragment key={loc.id}>
                                    <tr>
                                        <td>{loc.name}</td>
                                        <td>{loc.transactions.length}</td>
                                        <td>${formatCurrency(totals.total_remit)}</td>
                                        <td>${formatCurrency(totals.total_sale_price)}</td>
                                        <td>
                                            {loc.transactions.length > 0 && (
                                                <Button
                                                    className="button-gradient-bg"
                                                    onClick={() => toggleExpand(loc.id)}
                                                >
                                                    {expanded[loc.id] ? "Hide Transactions" : "View Transactions"}
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td colSpan={8} className="p-0 border-0">
                                            <Collapse isOpen={expanded[loc.id]}>
                                                <Table
                                                    bordered
                                                    size="sm"
                                                    responsive
                                                    className="mb-0 table-striped"
                                                >
                                                    <thead>
                                                        <tr>
                                                            <th>Date</th>
                                                            <th>Total Remit</th>
                                                            <th>Total Sale Price</th>
                                                            <th>License Plate</th>
                                                            <th>Type</th>
                                                            <th>Gross Remit</th>
                                                            <th>Adjustment / Reason</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {paginated.map((t) => (
                                                            <tr key={t.id}>
                                                                <td>{formatDate(t.transaction_date)}</td>
                                                                <td>${formatCurrency(t.total_remit)}</td>
                                                                <td>${formatCurrency(t.total_sale_price)}</td>
                                                                <td>{t.license_plate}</td>
                                                                <td>{t.type == "Event" ? t.type + " - " + t.event_venue : t.type}</td>
                                                                <td>${formatCurrency(t.gross_remit)}</td>
                                                                <td>${formatCurrency(t.manual_adjustment)}{t.refund_or_adjustment_reason != null ? " / " + t.refund_or_adjustment_reason : ""}</td>
                                                            </tr>
                                                        ))}
                                                        {loc.transactions.length === 0 && (
                                                            <tr>
                                                                <td colSpan="12" className="text-center py-3">
                                                                    No transactions found
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </Table>
                                                {loc.transactions.length > itemsPerPage && (
                                                    <div className="d-flex justify-content-between align-items-center p-2">
                                                        <Button
                                                            color="secondary"
                                                            size="sm"
                                                            onClick={() =>
                                                                setPage((prev) => ({
                                                                    ...prev,
                                                                    [loc.id]: Math.max(1, currentPage - 1),
                                                                }))
                                                            }
                                                            disabled={currentPage === 1}
                                                        >
                                                            Previous
                                                        </Button>
                                                        <span>
                                                            Page {currentPage} of {totalPages(loc.transactions)}
                                                        </span>
                                                        <Button
                                                            color="secondary"
                                                            size="sm"
                                                            onClick={() =>
                                                                setPage((prev) => ({
                                                                    ...prev,
                                                                    [loc.id]: Math.min(
                                                                        totalPages(loc.transactions),
                                                                        currentPage + 1
                                                                    ),
                                                                }))
                                                            }
                                                            disabled={currentPage === totalPages(loc.transactions)}
                                                        >
                                                            Next
                                                        </Button>
                                                    </div>
                                                )}
                                            </Collapse>
                                        </td>
                                    </tr>
                                </React.Fragment>
                            );
                        })}
                    {locations.length === 0 && (
                        <tr>
                            <td colSpan={8} className="text-center py-3">
                                No locations found
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>
        </div>
    );
};

SpotHeroReportTable.propTypes = {
    locations: PropTypes.array.isRequired,
};

export default SpotHeroReportTable;